import React from 'react';
import { Handshake, GraduationCap, Users, Crown, HeartHandshake, ArrowRight } from 'lucide-react';
import { motion } from 'framer-motion';
import { interventionsData } from '../data/mock';
import { useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';

const iconMap = { Handshake, GraduationCap, Users, Crown, HeartHandshake };

const Interventions = () => {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-cream">
      <Helmet>
        <title>Nos Axes d'Intervention - HPD</title>
        <meta name="description" content="Decouvrez les 5 axes d'intervention de HPD pour la paix et le developpement en RDC." />
      </Helmet>

      {/* Hero */}
      <section className="relative pt-32 pb-20 bg-navy overflow-hidden">
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
            <p className="uppercase text-xs md:text-sm tracking-[0.2em] font-bold text-forest mb-4 font-sans">5 Piliers Strategiques</p>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-serif font-bold text-white mb-4 tracking-tight">{interventionsData.title}</h1>
            <div className="w-16 h-1 bg-forest rounded-full mb-6" />
            <p className="text-lg text-white/70 max-w-3xl font-sans">{interventionsData.subtitle}</p>
          </motion.div>
        </div>
      </section>

      {/* Pillars */}
      <section className="py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="space-y-8">
            {interventionsData.pillars.map((pillar, index) => {
              const Icon = iconMap[pillar.icon];
              const isEven = index % 2 === 0;
              return (
                <motion.div
                  key={pillar.id}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5 }}
                  className="bg-white rounded-3xl overflow-hidden border border-black/5 shadow-sm hover:shadow-md transition-all group"
                >
                  <div className={`grid md:grid-cols-2 gap-0 ${!isEven ? 'md:grid-flow-dense' : ''}`}>
                    <div className={`relative h-64 md:h-80 overflow-hidden ${!isEven ? 'md:col-start-2' : ''}`}>
                      <img src={pillar.image} alt={pillar.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
                      <div className="absolute inset-0 bg-navy/10 group-hover:bg-navy/5 transition-colors" />
                    </div>
                    <div className="p-8 md:p-10 flex flex-col justify-center">
                      <div className={`w-12 h-12 rounded-xl bg-gradient-to-br ${pillar.color} flex items-center justify-center mb-5`}>
                        <Icon className="w-6 h-6 text-white" />
                      </div>
                      <div className="flex items-center space-x-3 mb-3">
                        <span className="text-xs font-bold text-gray-300 font-sans">0{pillar.id}</span>
                        <div className="flex-1 h-px bg-gray-100" />
                      </div>
                      <h3 className="text-2xl font-serif font-bold text-navy mb-3">{pillar.title}</h3>
                      <p className="text-base text-gray-600 leading-relaxed font-sans">{pillar.description}</p>
                    </div>
                  </div>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-24 bg-navy">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl sm:text-4xl font-serif font-bold text-white mb-4 tracking-tight">Rejoignez Notre Mission</h2>
          <p className="text-lg text-white/70 mb-10 leading-relaxed font-sans">
            Ensemble, construisons un avenir de paix et de prosperite pour toutes les communautes de la RDC
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <button onClick={() => navigate('/#contact')} className="inline-flex items-center justify-center px-8 py-4 rounded-full bg-white text-navy font-semibold hover:bg-gray-100 transition-all shadow-lg group font-sans">
              Nous Contacter <ArrowRight className="ml-2 w-5 h-5 group-hover:translate-x-1 transition-transform" />
            </button>
            <button onClick={() => navigate('/don')} className="inline-flex items-center justify-center px-8 py-4 rounded-full border-2 border-white/30 text-white font-semibold hover:bg-white hover:text-navy transition-all font-sans">
              Faire un Don
            </button>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Interventions;
